import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "./docUtils";

const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";

export default function ServicesSettings() {
  const [services, setServices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("unite");
  const [description, setDescription] = useState("");

  const load = () => {
    api.get("/accounting/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/accounting/quote-templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const add = async () => {
    if (!name.trim()) { toast.error("Nom de la prestation requis"); return; }
    try {
      await api.post("/accounting/services", {
        name: name.trim(), description: description.trim(),
        price_ht: parseFloat(String(price).replace(",", ".")) || 0, unit,
      });
      toast.success("Prestation enregistrée");
      setName(""); setPrice(""); setDescription(""); setUnit("unite");
      load();
    } catch { toast.error("Erreur"); }
  };

  const removeService = async (id) => {
    await api.delete(`/accounting/services/${id}`);
    toast.success("Prestation supprimée");
    load();
  };
  const removeTemplate = async (id) => {
    await api.delete(`/accounting/quote-templates/${id}`);
    toast.success("Modèle supprimé");
    load();
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5 space-y-4" data-testid="services-settings">
      <div>
        <h2 className="font-heading font-bold">Comptabilité — Prestations</h2>
        <p className="text-xs text-neutral-400 mt-0.5">
          Vos prestations habituelles : sélectionnables lors de la création d'un devis ou d'une facture (désignation et prix pré-remplis, modifiables).
        </p>
      </div>

      <div className="space-y-2" data-testid="services-list">
        {services.length === 0 && <p className="text-sm text-neutral-400">Aucune prestation enregistrée.</p>}
        {services.map((s) => (
          <div key={s.id} className="flex items-center gap-3 text-sm border border-neutral-100 rounded-lg px-3 py-2" data-testid={`service-row-${s.id}`}>
            <span className="flex-1 min-w-0 truncate font-medium">{s.name}</span>
            <span className="text-neutral-500 shrink-0">{eur(s.price_ht)}{s.unit === "mois" ? " / mois" : ""}</span>
            <button onClick={() => removeService(s.id)} className="text-neutral-300 hover:text-red-500 shrink-0" data-testid={`service-delete-${s.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="border-t border-neutral-100 pt-4 space-y-2">
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Community Management" className="mt-1" data-testid="service-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Prix HT (€)</Label>
              <Input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="690" className="mt-1" data-testid="service-price" />
            </div>
            <div>
              <Label className="text-xs">Facturation</Label>
              <select className={`${sel} mt-1`} value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="service-unit">
                <option value="unite">À l'unité</option>
                <option value="mois">Par mois</option>
              </select>
            </div>
          </div>
        </div>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optionnel)" data-testid="service-description" />
        <Button onClick={add} variant="outline" size="sm" className="gap-1.5" data-testid="service-add">
          <Plus className="w-4 h-4" /> Ajouter la prestation
        </Button>
      </div>

      {templates.length > 0 && (
        <div className="border-t border-neutral-100 pt-4 space-y-2">
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Modèles de devis</p>
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 text-sm border border-neutral-100 rounded-lg px-3 py-2" data-testid={`template-row-${t.id}`}>
              <span className="flex-1 min-w-0 truncate font-medium">{t.name}</span>
              <span className="text-neutral-400 text-xs shrink-0">{t.items.length} prestation(s)</span>
              <button onClick={() => removeTemplate(t.id)} className="text-neutral-300 hover:text-red-500 shrink-0" data-testid={`template-delete-${t.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <p className="text-xs text-neutral-400">Les modèles se créent depuis la modale de devis (« Enregistrer comme modèle »).</p>
        </div>
      )}
    </div>
  );
}
